/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-common
 * (C) Copyright IBM Corp. 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { omit } from 'lodash';
import { propsUtils } from '@ba-ui-toolkit/utils';
import {
	RadioButtonGroup as CarbonRadioButtonGroup,
	RadioButton as CarbonRadioButton,
} from 'carbon';

const { BaPropTypes } = propsUtils;

export default class RadioGroup extends Component {
	static propTypes = {
		/**
		 * Provide a collection of <RadioButton> components to render in the group
		 */
		children: PropTypes.node,

		/**
		 * Provide an optional className to be applied to the container node
		 */
		className: PropTypes.string,

		/**
		 * Specify the <RadioButton> to be selected by default
		 */
		defaultSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

		/**
		 * Legacy toolkit prop, map to orientation
		 */
		direction: PropTypes.oneOf(['horizontal', 'vertical']),

		/**
		 * Provide where radio buttons should be placed
		 */
		orientation: PropTypes.oneOf(['horizontal', 'vertical']),

		/**
		 * Provide where label text should be placed
		 */
		labelPosition: PropTypes.oneOf(['left', 'right']),

		/**
		 * Specify the name of the underlying <input> nodes
		 */
		name: PropTypes.string.isRequired,

		/**
		 * Specify whether the group is disabled
		 */
		disabled: PropTypes.bool,

		/**
		 * Provide an optional `onChange` hook that is called whenever the value of
		 * the group changes
		 */
		onChange: PropTypes.func,

		/**
		 * Legacy toolkit prop, map to valueSelected
		 */
		checked: PropTypes.any,

		/**
		 * Specify the value that is currently selected in the group
		 */
		valueSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

		/**
		 * Legacy toolkit prop, contains props for RadioButton
		 */
		options: PropTypes.array.isRequired,

		/**
		 * pass through toolkit props
		 */
		style: PropTypes.object,

		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		selectedId: BaPropTypes.deprecatedByCarbon(CarbonRadioButtonGroup),

		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		readOnly: BaPropTypes.deprecatedByCarbon(CarbonRadioButtonGroup),

		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		align: BaPropTypes.deprecatedByCarbon(CarbonRadioButtonGroup),
	};

	render() {
		const { orientation, direction, valueSelected, options, checked, ...rest } = this.props;

		const passThrouProps = omit(rest, ['selectedId', 'readOnly']);

		const mappedProps = {
			orientation: orientation || direction,
			valueSelected: valueSelected || checked,
		};

		const radioButtons = options.map(props => {
			const { label, ...radioPropsRest } = props;

			const passThrouRadioProps = omit(radioPropsRest, [
				'tabIndex',
				'title',
				'readOnly',
				'hover',
				'active',
				'aria-labelledby',
			]);

			const mappedRadioProps = {
				disabled: this.props.disabled || props.disabled,
				labelText: label,
			};

			return <CarbonRadioButton {...passThrouRadioProps} {...mappedRadioProps} />;
		});

		return (
			<CarbonRadioButtonGroup {...passThrouProps} {...mappedProps}>
				{radioButtons}
			</CarbonRadioButtonGroup>
		);
	}
}
